import { Grid, Typography, TypographyProps } from '@mui/material';
import { GroupCollapse } from '@/components/common/GroupCollapse';

// Label style object
const labelStyle = {
  color: '#344054', // Gray-700
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20px',
};

type DetailItem = {
  label: React.ReactNode;
  value: React.ReactNode;
};

type Props = {
  label?: React.ReactNode;
  backgroundColor?: string;
  size?: 'small' | 'default';
  children: React.ReactNode;
};

function LabelText({ children }: { children: React.ReactNode }) {
  return <Typography sx={labelStyle}>{children}</Typography>;
}

function ValueText({ sx, children, ...props }: TypographyProps) {
  return (
    <Typography
      {...props}
      sx={{
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 1,
        overflow: 'hidden',
        color: '#101828',
        textOverflow: 'ellipsis',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '24px',
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * Sub-component for rendering individual label-value pairs as a row.
 * Automatically applies appropriate styling based on content type (string vs React component).
 *
 * @param props - Component props
 * @param props.label - The label content - if string, will be wrapped in LabelText
 * @param props.value - The value content - if string, will be wrapped in ValueText
 * @returns A Grid row with label (xs=5) and value (xs=7) columns
 */
function Row({ label, value }: DetailItem) {
  return (
    <>
      <Grid item xs={5} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {typeof label === 'string' ? <LabelText>{label}</LabelText> : label}
      </Grid>
      <Grid
        item
        xs={7}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}
      >
        {typeof value === 'string' ? <ValueText>{value}</ValueText> : value}
      </Grid>
    </>
  );
}

/**
 * Component for displaying label-value pairs in a collapsible section.
 *
 * This component provides a consistent way to display structured information as label-value pairs
 * within a collapsible GroupCollapse container. It supports both simple string content and
 * complex React components for maximum flexibility.
 *
 * The component uses a compound pattern, providing sub-components (Row, LabelText, ValueText)
 * that can be used together to create rich, customizable detail displays.
 *
 * @param props - Component props
 * @param props.label - Optional header label for the collapsible section
 * @param props.backgroundColor - Optional background color for the section
 * @param props.size - Optional size variant ('small' or 'default')
 * @param props.children - Child components, typically NoteDetails.Row components
 *
 * @example
 * // Basic usage with Row components
 * <NoteDetails>
 *   <NoteDetails.Row label="Tag" value="Important" />
 *   <NoteDetails.Row label="Status" value="Active" />
 * </NoteDetails>
 *
 * @example
 * // Custom styling and complex content
 * <NoteDetails label="Custom Details" backgroundColor="#f0f0f0" size="default">
 *   <NoteDetails.Row
 *     label={<strong>Priority</strong>}
 *     value={<span style={{color: 'red'}}>High</span>}
 *   />
 *   <NoteDetails.Row
 *     label="Description"
 *     value={
 *       <NoteDetails.ValueText>
 *         <span dangerouslySetInnerHTML={{ __html: htmlContent }} />
 *       </NoteDetails.ValueText>
 *     }
 *   />
 * </NoteDetails>
 */
function NoteDetailsBase(props: Props) {
  const { label = 'Details', backgroundColor = '#F9F9F9', size = 'small', children } = props;

  return (
    <GroupCollapse label={label} backgroundColor={backgroundColor} size={size}>
      <Grid container rowSpacing="10px" columnSpacing="10px" justifyContent="space-between">
        {children}
      </Grid>
    </GroupCollapse>
  );
}

const NoteDetailsUI = Object.assign(NoteDetailsBase, {
  Row,
  ValueText,
  LabelText,
});

export { NoteDetailsUI };
